import { UseQueryOptions } from '@tanstack/react-query';

import { useAPI } from 'hooks/api/useAPI';
import { updateWorkspaceBooleanTrait } from 'services/customer_tracking';

import APIError from 'types/api_error';
import { Deployment, DeploymentState } from 'types/deployment';
import { OffsetPaginated, OffsetPaginationParams } from 'types/pagination';
import { TagParams } from 'tags/types/tagged';

import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';
import { useUser } from 'hooks/useAuth';

export type UseDeploymentsParams = TagParams & {
  deployment_ids?: Array<string>;
  deployment_names?: Array<string>;
  always_on?: boolean;
  states?: Array<DeploymentState>;
  video_source_ids?: Array<string>;
  video_source_names?: Array<string>;
  gateway_ids?: Array<string>;
  gateway_names?: Array<string>;
  pipeline_ids?: Array<string>;
  pipeline_names?: Array<string>;
  created_ts_since?: string;
  created_ts_until?: string;
  updated_ts_since?: string;
  updated_ts_until?: string;
  /** Private API parameters: this is a mitigation to counter https://www.notion.so/lumeo/High-Load-High-Data-transfer-5xx-2023-11-03-74d4e6a7221044c7a2234f9b9b03e78f
   * This should be removed in the future when https://app.shortcut.com/lumeo/story/9237/dedicated-internal-deployment-list-endpoint-for-console is implemented
   * */
  exclude_configuration?: boolean;
  exclude_definition?: boolean;
};

type UseDeploymentsOptions = UseQueryOptions<
  OffsetPaginated<Deployment>,
  APIError
> & {
  params?: UseDeploymentsParams & OffsetPaginationParams;
};

export function useDeployments({
  queryKey = ['deployments'],
  params,
  onSuccess,
  ...options
}: UseDeploymentsOptions = {}) {
  const { applicationID, client } = useAPI();
  const user = useUser();

  const queryParams: UseDeploymentsParams = {
    pagination: 'offset',
    page: 1,
    limit: 50,
    exclude_configuration: true,
    exclude_definition: true,
    ...params,
  };

  return useAuthenticatedQuery(
    [...queryKey, applicationID, queryParams],
    async () => {
      const { data } = await client.get(`/apps/${applicationID}/deployments`, {
        params: queryParams,
      });

      return data;
    },
    {
      ...options,
      onSuccess(data, ...args) {
        onSuccess?.(data, ...args);

        if (!user) {
          return;
        }

        updateWorkspaceBooleanTrait({
          userId: user.id,
          workspaceId: applicationID,
          trait: 'has_deployment',
          value: data.data.length > 0,
        });
      },
    }
  );
}
